'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { CONTROL_CLASS } from './input';
import { useAnchoredPanel } from './use-anchored-panel';

export interface ComboOption {
  value: string;
  label: string;
  /** Right-aligned muted context, e.g. a role or count. */
  hint?: string;
  /** Non-selectable — greyed out and skipped by pointer and keyboard. */
  disabled?: boolean;
}

interface BaseProps {
  options: ComboOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Accessible name for the trigger when there is no visible label. */
  'aria-label'?: string;
  /** Flags the trigger as invalid (red border + aria-invalid). */
  invalid?: boolean;
}

interface SingleProps extends BaseProps {
  multi?: false;
  value: string | null;
  onChange: (value: string | null) => void;
  clearable?: boolean;
}

interface MultiProps extends BaseProps {
  multi: true;
  values: string[];
  onChange: (values: string[]) => void;
}

type ComboboxProps = SingleProps | MultiProps;

/** Searchable picker for ≥8 options (agents, campaigns, tags). */
export function Combobox(props: ComboboxProps) {
  const { options, placeholder = 'Select…', disabled, className } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const panelStyle = useAnchoredPanel(open, rootRef, panelRef, close, { estimatedHeight: 300 });

  const selectedValues = props.multi ? props.values : props.value ? [props.value] : [];
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.hint?.toLowerCase().includes(q)
    );
  }, [options, query]);

  // First enabled row in the current filtered set — where the highlight should rest.
  const firstEnabled = useMemo(() => {
    const i = filtered.findIndex((o) => !o.disabled);
    return i === -1 ? 0 : i;
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(firstEnabled);
    searchRef.current?.focus();
  }, [open, firstEnabled]);

  useEffect(() => setHighlight(firstEnabled), [query, firstEnabled]);

  const pick = (value: string) => {
    if (options.find((o) => o.value === value)?.disabled) return;
    if (props.multi) {
      props.onChange(
        selectedSet.has(value)
          ? props.values.filter((v) => v !== value)
          : [...props.values, value]
      );
    } else {
      props.onChange(value);
      setOpen(false);
    }
  };

  const labelFor = (value: string) => options.find((o) => o.value === value)?.label ?? value;

  // Step past disabled options so arrow keys never land on an unselectable row.
  const nextEnabled = (from: number, step: number) => {
    for (let i = from; i >= 0 && i < filtered.length; i += step) {
      if (!filtered[i].disabled) return i;
    }
    return from;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => nextEnabled(Math.min(h + 1, filtered.length - 1), 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => nextEnabled(Math.max(h - 1, 0), -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered.at(highlight);
      if (opt && !opt.disabled) pick(opt.value);
    }
  };

  return (
    <div ref={rootRef} className={cn('relative min-w-0', className)}>
      <button
        type="button"
        disabled={disabled}
        aria-label={props['aria-label']}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={props.invalid || undefined}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          CONTROL_CLASS,
          'flex items-center gap-1 overflow-hidden text-left',
          props.invalid && 'border-danger focus:border-danger'
        )}
      >
        {selectedValues.length === 0 ? (
          <span className="truncate text-zinc-600">{placeholder}</span>
        ) : props.multi ? (
          <span className="flex min-w-0 flex-wrap items-center gap-1">
            {selectedValues.map((v) => (
              <span
                key={v}
                className="corner-brand-xs inline-flex items-center gap-1 bg-white/5 px-1.5 text-xs leading-4 text-zinc-300"
              >
                {labelFor(v)}
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Remove ${labelFor(v)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    pick(v);
                  }}
                  className="hover:text-zinc-50"
                >
                  <X className="size-2.5" aria-hidden />
                </span>
              </span>
            ))}
          </span>
        ) : (
          <span className="truncate">{labelFor(selectedValues[0])}</span>
        )}
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {!props.multi && props.clearable && props.value ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear"
              onClick={(e) => {
                e.stopPropagation();
                props.onChange(null);
              }}
              className="text-zinc-500 hover:text-zinc-200"
            >
              <X className="size-3" aria-hidden />
            </span>
          ) : null}
          <ChevronDown className="size-3.5 text-zinc-500" aria-hidden />
        </span>
      </button>
      {open && panelStyle
        ? createPortal(
        <div
          ref={panelRef}
          data-floating-panel
          style={{ ...panelStyle, width: rootRef.current?.getBoundingClientRect().width ?? 280 }}
          className="corner-brand-sm border border-zinc-700 bg-zinc-900 shadow-xl shadow-black/40"
        >
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search…"
            className="h-8 w-full border-b border-zinc-800 bg-transparent px-2.5 text-sm text-zinc-100 outline-none placeholder:text-zinc-600"
          />
          <div className="thin-scroll max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-2.5 py-2 text-xs text-zinc-500">No matches.</p>
            ) : (
              filtered.map((opt, i) => {
                const selected = selectedSet.has(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => pick(opt.value)}
                    onMouseEnter={() => !opt.disabled && setHighlight(i)}
                    className={cn(
                      'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm transition-colors',
                      i === highlight && !opt.disabled && 'bg-white/5',
                      opt.disabled
                        ? 'cursor-not-allowed text-zinc-600'
                        : selected
                          ? 'text-accent-bright'
                          : 'text-zinc-200'
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                    {opt.hint ? (
                      <span className="shrink-0 text-xs text-zinc-500">{opt.hint}</span>
                    ) : null}
                    {selected ? <Check className="size-3.5 shrink-0" aria-hidden /> : null}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body)
        : null}
    </div>
  );
}
